import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder } from '@angular/forms';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ButtonDirective } from '../../directives/button.directive';
import { FlexSpacerDirective } from '../../directives/flex-spacer.directive';
import { ModelTypeKeys } from 'app/enums/model-type-keys.enum';
import { FormSubmission } from 'app/models/entities/forms/form-submission';
import { ViolationStatement } from 'app/models/entities/violation-statement';
import { UploadedFile } from 'app/models/entities/uploaded-file';
import { RequestFormService } from 'app/services/forms/request-form.service';
import { ViolationsService } from 'app/services/violations/violations.service';
import { UploadedFilesService } from 'app/services/uploaded-files.service';
import { GrowlerService } from 'app/services/growler.service';
import { AvatarBlockModule } from '../../avatar-block/avatar-block.module';
import { MatDialogSizes } from 'app/enums/mat-dialog-sizes.enum';
import { MultiDocumentUploaderDialogComponent, MultiDocumentUploaderDialogInput } from '../../../../app-layout/shared/multi-document-uploader-dialog/multi-document-uploader-dialog.component';
import { EmptyStateModule } from '../../empty-state/empty-state.module';
import { ConfirmActionV2DialogComponent, ConfirmActionV2DialogInput } from '../../dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { GenericDialogResult } from '../../../../app-layout/shared/generic-mat-dialog/generic-dialog-result';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmActionDialogComponent, ConfirmActionDialogInput } from '../../../../app-layout/shared/confirm-action-dialog/confirm-action-dialog.component';

@UntilDestroy()
@Component({
    selector: 'app-attachments-card',
    templateUrl: './attachments-card.component.html',
    standalone: true,
    host: { class: 'attachments-card' },
    imports: [
        NgIf,
        NgForOf,
        MatCardModule,
        MatButtonModule,
        MatProgressBarModule,
        MatListModule,
        ButtonDirective,
        MatToolbarModule,
        FlexSpacerDirective,
        MatIconModule,
        AvatarBlockModule,
        MatDialogModule,
        EmptyStateModule,
    ],
})
export class AttachmentsCardComponent implements OnChanges
{
    @Input() modelType: ModelTypeKeys;
    @Input() model: FormSubmission | ViolationStatement;
    @Input() isSaving = false;

    formGroup = this._fb.group({
        id: null,
    });
    loadingInitialData = true;
    refreshingData = false;
    addAttachmentsEnabled = false;
    attachments: UploadedFile[];

    constructor(
            private _requestFormService: RequestFormService,
            private _violationsService: ViolationsService,
            private _uploadedFilesService: UploadedFilesService,
            private _fb: FormBuilder,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.hasOwnProperty('model') && this.model) {
            this.refreshAttachments();
        }
    }

    refreshAttachments() {
        let obs: Observable<UploadedFile[]>;
        switch (this.modelType) {
            case ModelTypeKeys.FORM_SUBMISSION:
                obs = this._requestFormService.loadAttachments(this.model.id, 'submission');
                break;
            case ModelTypeKeys.VIOLATION:
                obs = this._violationsService.loadAttachments(this.model.id);
                break;
        }

        if (obs) {
            this.refreshingData = true;
            obs.pipe(
                    finalize(() => {
                        this.loadingInitialData = this.refreshingData = false;
                        this.formGroup.get('id').reset(null);
                    }),
                    untilDestroyed(this),
            ).subscribe({
                next: (res) => this.attachments = res || [],
                error: () => this._growler.oops('Unable to load attachments'),
            });
        }
    }

    getSvgIcon(attachment: UploadedFile) {
        const imageFiletypes = [
            'jpg',
            'jpeg',
            'tif',
            'tiff',
            'png',
            'gif',
        ];

        if (imageFiletypes.indexOf(attachment.icon) > -1) {
            return 'v2:fi-br-picture';
        }

        return 'v2:fi-br-file-invoice';
    }

    deleteAttachment(attachment: UploadedFile) {
        if (this.formGroup.get('id').value) {
            return;
        }

        let obs: Observable<Object>;
        let type = 'item';
        switch (this.modelType) {
            case ModelTypeKeys.FORM_SUBMISSION:
                obs = this._uploadedFilesService.unlink(attachment, 'submission', this.model.id);
                type = 'submission';
                break;
            case ModelTypeKeys.VIOLATION:
                obs = this._uploadedFilesService.unlink(attachment, 'violation', this.model.id);
                type = 'violation';
                break;
        }

        if (obs) {
            const data: ConfirmActionDialogInput = {
                title: `Delete attachment for this ${type}?`,
                content: 'This action cannot be undone.',
                confirmButton: 'Yes, delete attachment',
                cancelButton: 'No',
            };
            this._dialog.open(ConfirmActionDialogComponent, {
                panelClass: ['v2-dialog', 'confirmation-layout'],
                width: MatDialogSizes.SM,
                autoFocus: 'dialog',
                data,
            }).afterClosed().subscribe({
                next: (res) => {
                    if (res?.success) {
                        this.addAttachmentsEnabled = false;
                        this.formGroup.get('id').setValue(attachment.id);
                        obs.pipe(untilDestroyed(this)).subscribe({
                            next: () => {
                                this._growler.success('Success', 'Attachment deleted');
                                this.refreshAttachments();
                            },
                            error: () => {
                                this._growler.oops('Unable to delete attachment');
                                this.formGroup.get('id').reset(null);
                            },
                        });
                    }
                },
            });
        }
    }

    addFiles() {
        let data: MultiDocumentUploaderDialogInput;
        switch (this.modelType) {
            case ModelTypeKeys.FORM_SUBMISSION:
                data = {
                    type: 'formSubmission',
                    formSubmission: (this.model as FormSubmission),
                    uploadedFiles: this.attachments,
                    newStyles: true,
                };
                break;
            case ModelTypeKeys.VIOLATION:
                data = {
                    type: 'violation',
                    violation: (this.model as ViolationStatement),
                    uploadedFiles: this.attachments,
                    newStyles: true,
                };
                break;
        }

        this.addAttachmentsEnabled = true;
        this._dialog.open(MultiDocumentUploaderDialogComponent, {
            autoFocus: 'dialog',
            panelClass: ['legacy-dialog', 'uploader-layout'],
            width: MatDialogSizes.MD,
            data,
        }).afterClosed().subscribe({
            next: (result: GenericDialogResult) => {
                if (result?.success) {
                    this.refreshAttachments();
                }
            },
        });
    }
}
